<template>
  <div class="login page">
    <section class="login__brand">
      <div class="login__photo" :style="{ backgroundImage: `url(${campus})` }" aria-hidden="true"></div>
      <div class="login__brand-inner">
        <div class="login__uni">
          <span class="login__crest"><img src="@/assets/pictures/LogoUsarb.png" alt="" /></span>
          <span>Universitatea de Stat<br />„Alecu Russo” din Bălți</span>
        </div>
        <div>
          <h1 class="login__title">Suport</h1>
          <p class="login__lead">
            Demersuri pentru echipamente, consumabile și reparații. Le trimiteți, le aprobați și le urmăriți aici.
          </p>
        </div>
        <address class="login__contact">
          <strong>Direcția Tehnologii Informaționale</strong>
          <span>Campusul Universitar, Blocul 3, etajul 3, of. 369</span>
          <span>tel. +373 (231) 52-388</span>
          <span>operator.dti@usarb.md</span>
        </address>
      </div>
    </section>

    <section class="login__panel">
      <v-form class="login__form" @submit.prevent="login">
        <h2 class="login__heading">Autentificare</h2>
        <p class="login__hint">Contul dvs. este creat de Direcția Tehnologii Informaționale.</p>

        <v-text-field
          v-model="form.username"
          label="Nume de utilizator"
          prepend-inner-icon="mdi-account-outline"
          outlined
          autocomplete="username"
          autofocus
          :error-messages="formErrors.username"
        />
        <v-text-field
          v-model="form.password"
          :type="showPassword ? 'text' : 'password'"
          label="Parola"
          prepend-inner-icon="mdi-lock-outline"
          :append-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
          outlined
          autocomplete="current-password"
          :error-messages="formErrors.password"
          @click:append="showPassword = !showPassword"
        />

        <div v-if="form.errorMessage" class="login__error" role="alert">
          <v-icon small color="error">mdi-alert-circle-outline</v-icon>
          {{ form.errorMessage }}
        </div>

        <v-btn type="submit" block x-large class="btn-green login__submit" :loading="loading">
          Intră în cont
        </v-btn>
      </v-form>
    </section>
  </div>
</template>

<script>
import { loginUser } from "./api";
import { jwtDecode } from "jwt-decode";
import { safeRedirect } from "@/helper/helper";

export default {
  data() {
    return {
      showPassword: false,
      loading: false,
      campus: require("@/assets/pictures/usarb.jpg"),
      form: {
        username: "",
        password: "",
        errorMessage: "",
      },
      formErrors: {
        username: "",
        password: "",
      },
    };
  },
  methods: {
    async login() {
      this.validateForm();
      if (!this.form.username || !this.form.password) {
        return;
      }
      this.loading = true;
      this.form.errorMessage = "";
      try {
        const response = await loginUser({
          user_name: this.form.username.trim(),
          password: this.form.password,
        });
        const token = response.access_token;
        localStorage.setItem("token", token);
        this.$store.commit("SET_TOKEN", token);
        this.$store.commit("SET_USER", jwtDecode(token));
        this.$router.push(this.redirectTarget()).catch(() => {});
      } catch (error) {
        this.form.password = "";
        if (!error.response) {
          this.form.errorMessage = "Serverul nu răspunde. Încercați din nou peste un minut.";
        } else if (error.response.status === 429) {
          this.form.errorMessage = error.response.data?.message || "Prea multe încercări. Încercați din nou mai târziu.";
        } else {
          this.form.errorMessage = "Numele de utilizator sau parola nu sunt corecte.";
        }
      } finally {
        this.loading = false;
      }
    },
    // ?redirect= from the router or the 401 handler (a page opened before logging in): only a page of this app
    redirectTarget() {
      return safeRedirect(this.$route.query.redirect);
    },
    validateForm() {
      this.formErrors.username = this.form.username ? "" : "Introduceți numele de utilizator";
      this.formErrors.password = this.form.password ? "" : "Introduceți parola";
    },
  },
  watch: {
    "form.username"() {
      this.formErrors.username = "";
    },
    "form.password"() {
      this.formErrors.password = "";
    },
  },
};
</script>

<style scoped>
.login {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(380px, 1fr);
  min-height: 100vh;
  background: var(--paper);
}
.login__brand {
  position: relative;
  overflow: hidden;
  background: var(--pine-900);
  color: #fff;
}
.login__photo {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center 30%;
  filter: grayscale(1) contrast(1.1);
  opacity: 0.22;
  mix-blend-mode: luminosity;
}
.login__brand::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(14, 42, 33, 0.35) 0%, rgba(10, 31, 24, 0.92) 78%);
}
.login__brand-inner {
  position: relative;
  z-index: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 48px;
  padding: 44px clamp(28px, 5vw, 72px);
}
.login__uni {
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.35;
  color: rgba(255, 255, 255, 0.9);
}
.login__crest {
  display: grid;
  place-items: center;
  width: 72px;
  height: 56px;
  border-radius: 14px;
  background: #f4faf6;
}
.login__crest img {
  width: 60px;
}
.login__title {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: clamp(4.5rem, 10vw, 8.5rem);
  line-height: 0.9;
  letter-spacing: -0.05em;
  margin: 0 0 22px;
  color: var(--leaf);
}
.login__lead {
  max-width: 30ch;
  margin: 0;
  font-size: clamp(1.15rem, 1.6vw, 1.45rem);
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.92);
}
.login__contact {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-style: normal;
  font-size: 0.98rem;
  line-height: 1.55;
  color: rgba(195, 233, 211, 0.85);
}
.login__contact strong {
  color: #fff;
}
.login__panel {
  display: grid;
  place-items: center;
  padding: 40px 24px;
}
.login__form {
  width: 100%;
  max-width: 420px;
}
.login__heading {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 2.2rem;
  letter-spacing: -0.02em;
  margin: 0 0 6px;
  color: var(--ink);
}
.login__hint {
  margin: 0 0 28px;
  color: var(--ink-2);
  font-size: 1.02rem;
}
.login__error {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: -6px 0 18px;
  padding: 10px 14px;
  border-radius: 10px;
  background: var(--brick-bg);
  color: var(--brick);
  font-weight: 600;
}
.login__submit.v-btn {
  height: 56px !important;
  border-radius: 12px !important;
  font-size: 1.1rem;
}
@media (max-width: 900px) {
  .login {
    grid-template-columns: 1fr;
  }
  .login__brand-inner {
    padding: 28px 24px 32px;
    gap: 28px;
  }
  .login__contact {
    display: none;
  }
}
</style>
